import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import activityService from '@api/v1/activityService'
import EventForm from '@components/calendar/EventForm'
import useDocumentTitle from '@hooks/useDocumentTitle'
import { toast } from '@store/toastStore'
import useKeyboardShortcuts from '@hooks/useKeyboardShortcuts'

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const Calendar = () => {
  useDocumentTitle('Calendar')
  const queryClient = useQueryClient()
  const [currentDate, setCurrentDate] = useState(dayjs())
  const [showCreate, setShowCreate] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [editingEvent, setEditingEvent] = useState(false)

  const shortcuts = useMemo(() => [
    { key: 'n', meta: true, handler: () => setShowCreate(true) },
  ], [])
  useKeyboardShortcuts(shortcuts)

  const monthStart = currentDate.startOf('month')
  const monthEnd = currentDate.endOf('month')

  const { data: activitiesResp, isLoading } = useQuery({
    queryKey: ['calendarActivities', currentDate.format('YYYY-MM')],
    queryFn: () => activityService.getActivities({ limit: 500 }),
    staleTime: 2 * 60 * 1000,
  })

  const activities = activitiesResp?.activities || []

  const createMutation = useMutation({
    mutationFn: (data) => activityService.createActivity(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendarActivities'] })
      setShowCreate(false)
      toast.success('Event created')
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to create event')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => activityService.updateActivity(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendarActivities'] })
      setSelectedEvent(null)
      setEditingEvent(false)
      toast.success('Event updated')
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to update event')
    },
  })

  // Group activities by date
  const eventsByDate = useMemo(() => {
    const map = {}
    for (const item of activities) {
      const dateStr = item.scheduledFor || item.createdDate || ''
      if (!dateStr) continue
      const key = dayjs(dateStr).format('YYYY-MM-DD')
      if (!map[key]) map[key] = []
      map[key].push(item)
    }
    return map
  }, [activities])

  // Build calendar grid
  const calendarDays = useMemo(() => {
    const days = []
    const startDay = monthStart.day()
    const daysInMonth = monthEnd.date()
    const prevMonthEnd = monthStart.subtract(1, 'day')

    for (let i = startDay - 1; i >= 0; i--) {
      const d = prevMonthEnd.subtract(i, 'day')
      days.push({ date: d, isCurrentMonth: false })
    }

    for (let i = 1; i <= daysInMonth; i++) {
      const d = monthStart.date(i)
      days.push({ date: d, isCurrentMonth: true })
    }

    const remaining = 42 - days.length
    for (let i = 1; i <= remaining; i++) {
      const d = monthEnd.add(i, 'day')
      days.push({ date: d, isCurrentMonth: false })
    }

    return days
  }, [monthStart, monthEnd])

  const today = dayjs().format('YYYY-MM-DD')

  const goToPrev = () => setCurrentDate((d) => d.subtract(1, 'month'))
  const goToNext = () => setCurrentDate((d) => d.add(1, 'month'))
  const goToToday = () => setCurrentDate(dayjs())

  const handleEventClick = (evt, e) => {
    e.stopPropagation()
    setSelectedEvent(evt)
    setEditingEvent(false)
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display text-gray-900">Calendar</h1>
          <p className="text-sm text-gray-500 mt-1">{currentDate.format('MMMM YYYY')}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowCreate(true)}
            className="px-4 py-2 bg-cb-accent text-white rounded-lg text-sm
              font-medium hover:bg-cb-accent/90 transition-colors shadow-sm mr-2"
          >
            + New Event
          </button>
          <button
            onClick={goToPrev}
            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            title="Previous month"
          >
            <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
          </button>
          <button
            onClick={goToToday}
            className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Today
          </button>
          <button
            onClick={goToNext}
            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            title="Next month"
          >
            <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        </div>
      </div>

      {/* Create Event Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-lg font-display text-gray-900 mb-4">New Event</h2>
            <EventForm
              onSave={(data) => createMutation.mutate(data)}
              onCancel={() => setShowCreate(false)}
              saving={createMutation.isPending}
            />
          </div>
        </div>
      )}

      {/* View/Edit Event Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
            {editingEvent ? (
              <>
                <h2 className="text-lg font-display text-gray-900 mb-4">Edit Event</h2>
                <EventForm
                  event={selectedEvent}
                  onSave={(data) => updateMutation.mutate({ id: selectedEvent.id, data })}
                  onCancel={() => setEditingEvent(false)}
                  saving={updateMutation.isPending}
                />
              </>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-display text-gray-900">
                    {selectedEvent.subject || selectedEvent.name || 'Event'}
                  </h2>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingEvent(true)}
                      className="px-3 py-1.5 text-sm font-medium text-white bg-cb-accent rounded-lg hover:bg-cb-accent/90 transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setSelectedEvent(null)}
                      className="px-3 py-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
                <dl className="space-y-3">
                  {selectedEvent.scheduledFor && (
                    <div>
                      <dt className="text-xs font-medium text-gray-500 uppercase mb-0.5">Scheduled</dt>
                      <dd className="text-sm text-gray-900">
                        {dayjs(selectedEvent.scheduledFor).format('MMM D, YYYY h:mm A')}
                      </dd>
                    </div>
                  )}
                  {selectedEvent.notes && (
                    <div>
                      <dt className="text-xs font-medium text-gray-500 uppercase mb-0.5">Notes</dt>
                      <dd className="text-sm text-gray-900 whitespace-pre-wrap">{selectedEvent.notes}</dd>
                    </div>
                  )}
                  {selectedEvent.matter && (
                    <div>
                      <dt className="text-xs font-medium text-gray-500 uppercase mb-0.5">Matter</dt>
                      <dd className="text-sm text-gray-900">{selectedEvent.matter}</dd>
                    </div>
                  )}
                  {selectedEvent.contact && (
                    <div>
                      <dt className="text-xs font-medium text-gray-500 uppercase mb-0.5">Contact</dt>
                      <dd className="text-sm text-gray-900">{selectedEvent.contact}</dd>
                    </div>
                  )}
                  {selectedEvent.owner && (
                    <div>
                      <dt className="text-xs font-medium text-gray-500 uppercase mb-0.5">Owner</dt>
                      <dd className="text-sm text-gray-900">{selectedEvent.owner}</dd>
                    </div>
                  )}
                </dl>
              </>
            )}
          </div>
        </div>
      )}

      {/* Calendar Grid */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Day headers */}
        <div className="grid grid-cols-7 border-b border-gray-100">
          {DAYS.map((day) => (
            <div key={day} className="px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase text-center">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar cells */}
        {isLoading ? (
          <div className="p-12 text-center text-sm text-gray-500">Loading calendar...</div>
        ) : (
          <div className="grid grid-cols-7">
            {calendarDays.map(({ date, isCurrentMonth }, idx) => {
              const key = date.format('YYYY-MM-DD')
              const events = eventsByDate[key] || []
              const isToday = key === today

              return (
                <div
                  key={idx}
                  className={`min-h-[100px] border-b border-r border-gray-50 p-1.5 ${
                    !isCurrentMonth ? 'bg-gray-50/50' : ''
                  }`}
                >
                  <div className={`text-xs font-medium mb-1 w-6 h-6 flex items-center justify-center rounded-full ${
                    isToday ? 'bg-cb-accent text-white' : isCurrentMonth ? 'text-gray-700' : 'text-gray-300'
                  }`}>
                    {date.date()}
                  </div>
                  <div className="space-y-0.5">
                    {events.slice(0, 3).map((evt, i) => (
                      <button
                        key={evt.id || i}
                        onClick={(e) => handleEventClick(evt, e)}
                        className="w-full text-left text-[10px] leading-tight px-1.5 py-0.5 rounded bg-cb-accent/10 text-cb-accent-text truncate hover:bg-cb-accent/20 transition-colors"
                        title={evt.subject || evt.name || ''}
                      >
                        {evt.subject || evt.name || 'Event'}
                      </button>
                    ))}
                    {events.length > 3 && (
                      <div className="text-[10px] text-gray-500 px-1.5">+{events.length - 3} more</div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default Calendar
